import type { ResolvedCatalogFlags, ResolvedCatalogLabels } from '@eshop/hooks/useSiteLabels.ts';
import type { Item } from '@eshop/types/catalogue.types.ts';
import { resolveCanBuy } from '@eshop/utils/stockPolicy.ts';
import { deriveStockPresentation, type StockPresentation } from '@eshop/utils/stockPresentation.ts';

interface ResolveCataloguePurchaseStateParams {
  baseItem: Item;
  currentItem: Item;
  hasVariants: boolean;
  hasSelectedVariant: boolean;
  erpEnabled: boolean;
  blockSalesWithoutStock: boolean;
  labels: ResolvedCatalogLabels;
  flags: ResolvedCatalogFlags;
  forceCanBuyFromPolicy?: boolean;
}

interface ResolveCataloguePurchaseStateResult {
  stockPresentation: StockPresentation;
  requiresVariantSelection: boolean;
  canPurchase: boolean;
  effectiveStock: number | undefined;
}

export const resolveBlockedCatalogueMessage = (
  stockPresentation: StockPresentation,
  labels: ResolvedCatalogLabels,
): string | null =>
  stockPresentation.message ??
  (stockPresentation.policyEnforced ? labels.blockedMessage : labels.oversaleMessage);

export const resolveCataloguePurchaseState = ({
  baseItem,
  currentItem,
  hasVariants,
  hasSelectedVariant,
  erpEnabled,
  blockSalesWithoutStock,
  labels,
  flags,
  forceCanBuyFromPolicy = false,
}: ResolveCataloguePurchaseStateParams): ResolveCataloguePurchaseStateResult => {
  const stockPolicy = resolveCanBuy({
    erpEnabled,
    blockSalesWithoutStock,
    item: {
      stock: currentItem.stock,
      stock_state: currentItem.stock_state,
      can_buy: currentItem.can_buy,
      disponibilidad: currentItem.disponibilidad,
    },
  });

  const candidateWithPolicy = {
    ...currentItem,
    can_buy: stockPolicy.canBuy,
    stock_state: stockPolicy.stockState ?? currentItem.stock_state,
  } as Item;

  const fallbackWithPolicy = {
    ...baseItem,
    can_buy: stockPolicy.canBuy,
    stock_state: stockPolicy.stockState ?? baseItem.stock_state,
  } as Item;

  const rawPresentation = deriveStockPresentation(candidateWithPolicy, fallbackWithPolicy, {
    policyEnforced: stockPolicy.policyEnforced,
    isBlockedByPolicy: stockPolicy.isBlockedByStock,
    copy: { labels, flags },
  });

  const stockPresentation = forceCanBuyFromPolicy
    ? ({ ...rawPresentation, canBuy: stockPolicy.canBuy } as StockPresentation)
    : rawPresentation;

  const requiresVariantSelection = hasVariants && !hasSelectedVariant;
  const canPurchase = stockPresentation.canBuy && !requiresVariantSelection;
  const effectiveStock = typeof currentItem.stock === 'number' ? currentItem.stock : undefined;

  return {
    stockPresentation,
    requiresVariantSelection,
    canPurchase,
    effectiveStock,
  };
};

